/**
 * Links from the support views into the customer-facing app.
 *
 * These used to be built from the Keycloak realm (`https://${realm}.persolog.online/...`), which only
 * works on dev by coincidence: the production realm is `prod` and staging's is `demo`, so the links
 * pointed at hosts that do not exist. The backend base URL is correct in every environment, and the
 * public app is served on the same host without the `backend.` prefix.
 */

/**
 * Turns a backend base URL into the base URL of the customer-facing app:
 * `https://backend.dev.persolog.online` -> `https://dev.persolog.online`,
 * `https://backend.persolog.online` -> `https://persolog.online`.
 * Local and CI hosts carry no such prefix (or nothing would be left of them) and are returned as they are.
 */
export function publicAppBaseUrl(backendBaseUrl: string): string {
    try {
        const url = new URL(backendBaseUrl);
        const withoutPrefix = url.hostname.replace(/^backend\./, "");
        if (withoutPrefix) {
            url.hostname = withoutPrefix;
        }
        return url.origin;
    } catch {
        // A malformed or empty base URL must not produce a half-built link.
        return "";
    }
}

/**
 * The participant's questionnaire in the customer-facing app. Returns an empty string without a hash,
 * so callers can hide the link instead of rendering one that leads nowhere.
 */
export function questionnaireUrl(questionnaireHash: string | undefined | null, backendBaseUrl?: string): string {
    if (!questionnaireHash) {
        return "";
    }
    const baseUrl = publicAppBaseUrl(backendBaseUrl ?? import.meta.env.VITE_APP_BACKEND_API_BASE_URL ?? "");
    return baseUrl ? `${baseUrl}/questionnaire/${questionnaireHash}` : "";
}
